import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import * as Icons from 'lucide-react';
import { useSiteConfig, resolveUploadUrl } from '../utils/siteConfig';
import { ServiceItem } from '../utils/data';

export function Services() {
  const { config, openGetStarted } = useSiteConfig();
  const services = [...config.services].reverse();
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(null);

  return (
    <div className="pt-24 bg-slate-50 min-h-screen">
      <motion.section
        className="py-16 px-4 md:px-8 relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.05 }}
        transition={{ duration: 0.8 }}
      >
        {/* Decorative blobs */}
        <motion.div
          className="absolute top-20 right-0 w-72 h-72 bg-gradient-to-br from-primary/10 to-primary/10 rounded-full blur-3xl pointer-events-none"
          animate={{ x: [0, -40, 0], y: [0, 40, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section heading */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="px-3 py-1 bg-primary/5 border border-primary/20 text-primary rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3 shadow-sm">
              Our Core Services
            </span>
            <h2 className="text-3xl md:text-5xl mb-4 text-slate-900 font-black tracking-tight heading-glow">What We Do Best</h2>
            <p className="text-base text-slate-600 max-w-xl mx-auto leading-relaxed font-sans">
              Comprehensive digital solutions tailored to build powerful brands, accelerate user acquisition, and drive long-term business growth.
            </p>
          </motion.div>

          {/* Service cards — 3 columns on desktop */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = (Icons as Record<string, any>)[service.icon] || Icons.Zap;
              return (
                <motion.div
                  key={index}
                  onClick={() => setSelectedService(service)}
                  className="group cursor-pointer"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <div className="rounded-[2.5rem] overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col hover:-translate-y-1.5 relative">
                    
                    {/* Click hint */}
                    <div className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm border border-slate-200/60 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                      <Icons.Expand size={13} className="text-slate-500" />
                    </div>

                    {/* Image */}
                    <div className="h-52 overflow-hidden relative bg-slate-50 border-b border-slate-100">
                      {service.image ? (
                        <img
                          src={resolveUploadUrl(service.image)}
                          alt={service.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-primary/10 border border-primary/15 rounded-xl flex items-center justify-center shadow-sm">
                            <IconComponent size={28} className="text-primary" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Content */}
                    <div className="p-8 flex flex-col flex-grow bg-white">
                      <div className="flex items-center gap-3.5 mb-4">
                        <div className="w-9 h-9 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-center text-primary shadow-sm shrink-0">
                          <IconComponent size={18} />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 leading-tight">{service.title}</h3>
                      </div>
                      
                      <p className="text-slate-500 text-sm leading-relaxed font-sans flex-grow line-clamp-3">
                        {service.description}
                      </p>

                      {/* Expand CTA */}
                      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-primary">
                        Read More <Icons.ArrowRight size={12} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Additional High-Value Services Section */}
      <section className="py-24 px-4 md:px-8 bg-white border-t border-slate-100 relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-primary/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/3 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section heading */}
          <div className="text-center mb-16">
            <span className="px-3 py-1 bg-primary/5 border border-primary/20 text-primary rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3 shadow-sm">
              More Offerings
            </span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-slate-900 mb-4 heading-glow">
              Additional High-Value Services
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full mt-4" />
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 gap-6 w-full">
            {[...(config.additionalServices || [])].reverse().map((svc, i) => {
              const IconComponent = (Icons as Record<string, any>)[svc.icon] || Icons.Zap;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  onClick={openGetStarted}
                  className="group bg-white border border-slate-100 rounded-[2rem] shadow-sm hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 hover:-translate-y-1.5 transition-all duration-300 p-8 cursor-pointer flex flex-col md:flex-row md:items-center gap-6"
                >
                  {/* Icon */}
                  <div
                    className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: `color-mix(in srgb, var(--primary) 10%, transparent)` }}
                  >
                    <IconComponent size={32} style={{ color: `var(--primary)` }} />
                  </div>

                  {/* Content Container */}
                  <div className="flex flex-col flex-grow gap-2 md:gap-3">
                    {/* Title in gradient brand colors */}
                    <h3
                      className="text-2xl font-extrabold leading-snug bg-clip-text text-transparent"
                      style={{ backgroundImage: `linear-gradient(135deg, var(--primary), var(--accent))` }}
                    >
                      {svc.title}
                    </h3>

                    {/* Description */}
                    <p className="text-slate-500 text-sm md:text-base leading-relaxed font-sans max-w-4xl">
                      {svc.description}
                    </p>
                  </div>

                  {/* CTA */}
                  <div
                    className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest mt-4 md:mt-0 opacity-60 group-hover:opacity-100 transition-all duration-300 shrink-0 bg-primary/5 px-6 py-3 rounded-full group-hover:bg-primary text-primary group-hover:text-white"
                  >
                    Get Started <Icons.ArrowRight size={16} />
                  </div>
                </motion.div>
              );
            })}
            {(!config.additionalServices || config.additionalServices.length === 0) && (
              <div className="col-span-3 text-center py-16 text-slate-400">No additional services configured.</div>
            )}
          </div>
        </div>
      </section>

      {/* FULLSCREEN MODAL FOR SELECTED SERVICE */}
      <AnimatePresence>
        {selectedService && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-slate-900/65 backdrop-blur-md"
            onClick={() => setSelectedService(null)}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', bounce: 0.28, duration: 0.45 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-[2.5rem] overflow-hidden shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col md:flex-row relative border border-slate-100"
            >
              {/* Back Button */}
              <button 
                onClick={() => setSelectedService(null)}
                className="absolute top-5 right-5 md:top-6 md:left-6 md:right-auto z-10 w-9 h-9 md:w-auto md:h-auto md:px-5 md:py-2.5 bg-white/90 backdrop-blur-md hover:bg-slate-50 text-slate-800 rounded-full border border-slate-200/80 shadow-md hover:shadow-lg transition-all flex items-center justify-center md:justify-start gap-2 font-semibold text-sm cursor-pointer"
                aria-label="Close"
              >
                <Icons.X size={16} className="md:hidden" />
                <Icons.ArrowLeft size={16} className="hidden md:block" /> <span className="hidden md:inline">Back</span>
              </button>

              {/* Left/Top: Large Image */}
              <div className="w-full md:w-3/5 h-48 sm:h-64 md:h-auto min-h-[220px] sm:min-h-[300px] md:min-h-[500px] relative bg-slate-50 shrink-0 border-b md:border-b-0 md:border-r border-slate-100">
                {selectedService.image ? (
                  <img 
                    src={resolveUploadUrl(selectedService.image)} 
                    alt={selectedService.title} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-primary/10 to-primary/10 border border-primary/15 rounded-3xl flex items-center justify-center shadow-sm">
                      {(() => {
                        const IconComponent = (Icons as Record<string, any>)[selectedService.icon] || Icons.Zap;
                        return <IconComponent size={48} className="text-primary" />;
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Right/Bottom: Content Details */}
              <div className="w-full md:w-2/5 p-8 md:p-12 overflow-y-auto flex flex-col justify-between bg-white max-h-[90vh] md:max-h-auto">
                <div className="space-y-6 pt-4 md:pt-0">
                  <span className="text-xs font-bold text-primary uppercase tracking-widest block">
                    Core Service
                  </span>
                  
                  <h3 className="text-3xl font-black text-slate-900 leading-tight">
                    {selectedService.title}
                  </h3>
                  
                  <hr className="border-slate-100" />
                  
                  <div>
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">Service Details</h4>
                    <p className="text-slate-600 text-sm md:text-base leading-relaxed font-sans whitespace-pre-line">
                      {selectedService.description}
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-100 flex gap-4">
                  <button 
                    onClick={() => { setSelectedService(null); openGetStarted(); }}
                    className="w-full py-3.5 bg-primary hover:opacity-90 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all text-center cursor-pointer flex items-center justify-center gap-2"
                  >
                    Get Started <Icons.ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
